import React, { useState } from 'react'
import {
  Form,
  Textarea,
  Input,
  Checkbox,
  Button,
  Select,
  useTraversal,
} from '@guillotinaweb/react-gmi'
import { I18NField } from '../../../components/i18nfield'
import { SelectTaxonomy } from '../../../components/select-taxonomy'
import { DatePicker } from '../../../common/date'
import { ImageEditor } from '../../../common/field'
import { PreviewWebsites } from './Preview'
import { usePatchLanding } from './usePatchLanding'
import {
  fixSlugs,
  isUnusualLandingSlug,
  landingPublicPath,
  landingRedirectsPath,
  slugifyLandingValue,
} from '../../landingSlug'

const Empty = () => null

export function Data({ state }) {
  return (
    <>
      <I18NField Widget={Empty}>
        {(lang) => (
          <>
            <PreviewWebsites currentLang={lang} data={state.context} />
            <DataForm lang={lang} {...state.context} />
          </>
        )}
      </I18NField>
    </>
  )
}

function DataForm(props) {
  const { lang } = props
  const [title, setTitle] = useState(props.title)
  const [slug, setSlug] = useState(props.slug || {})
  const [template, setTemplate] = useState(props.template)
  const [loading, saveLanding] = usePatchLanding()
  const [description, setDescription] = useState(props.description)
  const [labels, setLabels] = useState(props.labels || [])
  const [postAuthor, setPostAuthor] = useState(props.post_author)
  const [publishDate, setPublishDate] = useState(props.publish_date)
  const [forLangs, setForLangs] = useState(props.for_langs)
  const [searchable, setSearchable] = useState(props.searchable)
  const setWithLang = (fn) => (v) => fn((o) => ({ ...o, [lang]: v }))

  const ctx = useTraversal()

  const landingTemplates = [
    { value: 'landing', text: 'Landing' },
    { value: 'blog-post', text: 'Blog Post' },
  ]

  async function onSubmit() {
    const payloadSlug = {}
    const unusual = []
    for (const [code, value] of Object.entries(slug || {})) {
      if (!value) {
        if ((props.slug || {})[code]) {
          ctx.flash(`Slug for ${code.toUpperCase()} cannot be empty`, 'danger')
          return
        }
        continue
      }
      if (isUnusualLandingSlug(value)) {
        payloadSlug[code] = value
        unusual.push([code, value])
      } else {
        payloadSlug[code] = fixSlugs({ [code]: value })[code]
      }
    }
    if (!payloadSlug[lang]) {
      ctx.flash('Slug cannot be empty', 'danger')
      return
    }
    if (
      unusual.length > 0 &&
      !window.confirm(
        `Hay slugs que no tienen el formato aaa-bb-ccc-ddd. ¿Estás seguro de guardarlos así?\n\n${unusual
          .map(([code, value]) => `${code}: ${value}`)
          .join('\n')}`
      )
    ) {
      return
    }

    const original = props.slug || {}
    const redirects = Object.keys(payloadSlug)
      .filter((code) => payloadSlug[code] !== (original[code] || ''))
      .map((code) => ({
        source: landingPublicPath(original[code], code),
        destination: landingPublicPath(payloadSlug[code], code),
      }))
      .filter(
        (redirect) =>
          redirect.source &&
          redirect.destination &&
          redirect.source !== redirect.destination
      )

    const createRedirects =
      redirects.length > 0 &&
      window.confirm(
        `The slug changed. Create a 301 redirect from the old URL to the new one?\n\n${redirects
          .map((redirect) => `${redirect.source} → ${redirect.destination}`)
          .join('\n')}`
      )

    const saved = await saveLanding({
      title,
      slug: payloadSlug,
      description,
      forLangs,
      template,
      labels,
      postAuthor,
      publishDate,
      searchable,
    })
    if (!saved || !createRedirects) return

    for (const redirect of redirects) {
      const resp = await ctx.client.post(landingRedirectsPath(ctx.path), {
        sources: [redirect.source],
        destination: redirect.destination,
      })
      if (resp.status >= 400) {
        let message = 'Slug saved, but the redirect could not be created'
        try {
          const body = await resp.json()
          message = body.reason || body.message || message
        } catch (e) {
          // The response body is not JSON.
        }
        ctx.flash(message, 'danger')
        return
      }
    }
    ctx.flash('301 redirect created', 'success')
  }

  function reset() {
    setTitle(props.title)
    setDescription(props.description)
    setSlug(props.slug || {})
  }

  return (
    <Form className="template-data-form" onSubmit={onSubmit}>
      <Checkbox
        children=" Landing enabled"
        key={`landing-enabled-${lang}`}
        value={(forLangs || []).includes(lang)}
        onChange={(v) =>
          v
            ? setForLangs([...forLangs, lang])
            : setForLangs([...forLangs].filter((l) => l !== lang))
        }
      />
      <Checkbox
        children=" Searchable"
        key={`searchable`}
        value={searchable}
        onChange={setSearchable}
      />
      <Input
        id="title"
        placeholder="Title"
        value={title[lang]}
        onChange={setWithLang(setTitle)}
      />
      <label className="label">Landing type</label>
      <Select
        onChange={(e) => setTemplate(e.target.value)}
        value={template || ''}
        options={landingTemplates}
      />
      <Input
        id="slug"
        required
        placeholder="Slug"
        value={slug[lang] || ''}
        onChange={(value) =>
          setSlug((current) => ({
            ...current,
            [lang]: isUnusualLandingSlug(value)
              ? value
              : slugifyLandingValue(value),
          }))
        }
      />
      <p className="slug-note">
        Un slug <code>aaaa-bbb-ccc</code> se publica como{' '}
        <code>/{lang}/content/aaaa-bbb-ccc/</code>. Si empieza por{' '}
        <code>/</code> (por ejemplo <code>/es/vermut/</code> o{' '}
        <code>/de/vermouths.html</code>), esa ruta es la URL y no se le añade{' '}
        <code>/content/</code>.
      </p>
      <label className="label">Description</label>
      <Textarea
        placeholder="Description"
        value={description[lang]}
        onChange={setWithLang(setDescription)}
      />
      {template === 'blog-post' && (
        <>
          <h2>Blog post specific fields</h2>
          <label className="label">Thumbnail</label>
          <ImageEditor
            Ctx={ctx}
            canModifyContent={true}
            uploadFieldName={'thumbnail_src'}
            field={props.thumbnail_src}
            folder={`landings/thumb_${props.id}`}
          />
          <label className="label">Author</label>
          <SelectTaxonomy
            taxonomy="post_authors"
            value={postAuthor}
            multiple={false}
            onChange={setPostAuthor}
            placeholder="Post Author"
          />
          <label className="label">Labels</label>
          <SelectTaxonomy
            taxonomy="post_labels"
            value={labels}
            multiple={true}
            onChange={(v) => setLabels(v)}
            placeholder="Post Author"
          />
          <DatePicker
            value={publishDate}
            name="Publish date"
            onUpdate={(e) => setPublishDate(e.target.value)}
          />
        </>
      )}
      <hr />
      <div className="landings-btn-controls">
        <Button
          disabled={loading}
          onClick={reset}
          type="button"
          className="is-normal"
        >
          Reset
        </Button>
        <Button loading={loading} className="is-primary">
          SAVE AND PUBLISH
        </Button>
      </div>
    </Form>
  )
}
